import { useAppContext } from '../../shared';
import { formatDate } from '../../utilities';
import ViewStateModal from './ViewStateModal';
import { Slug, Button } from '../../components';
import { Form, FormItem, TopPanel } from '../../components';
import { useViewContactState } from './useViewContactState';
import { SearchBar, Dropdown, Table } from '../../components';
import { useCampaignMonitoring } from './useCampaignMonitoring';
import { getContactStateColor as getColor } from '../../utilities';

const CampaignMonitoring = () => {
  const { meta } = useAppContext();
  const {
    nodes,
    refresh,
    filters,
    setFilters,
    metrics,
    reloading,
    initializing: loading,
    sort,
    setSort,
  } = useCampaignMonitoring();
  const {
    state,
    onClose,
    isOpened,
    contactId,
    loading: loadingView,
    handleView,
  } = useViewContactState();

  const contact = nodes.find(n => n.contact_id === contactId);

  return (
    <div className="flex flex-col gap-y-md w-full">
      <ViewStateModal
        isOpened={isOpened}
        state={state}
        contact={contact}
        onClose={onClose}
      />
      <TopPanel>
        <div className="mr-auto flex items-center gap-x-md min-w-[20rem]">
          Human Appointments Booked:
          <Slug text={metrics?.appointments_booked || 0} className="w-[3rem]" />
        </div>
        <Form<NonNullable<typeof filters>>
          initialValue={filters}
          onChange={setFilters}
        >
          <FormItem<NonNullable<typeof filters>> name="search" label="">
            <SearchBar />
          </FormItem>
          <FormItem<NonNullable<typeof filters>>
            label=""
            name="latest_contact_state_enum"
          >
            <Dropdown<LatestContactState>
              grow={false}
              placeholder="All Statuses"
              options={[
                { value: undefined, label: 'All Statuses' },
                { value: 'active', label: 'Active' },
                { value: 'READY_FOR_OUTREACH', label: 'Ready For Outreach' },
                { value: 'OUTREACH_FAILED', label: 'Outreach Failed' },
                { value: 'goal_reached', label: 'Goal Reached' },
                { value: null, label: 'No State' },
              ]}
            />
          </FormItem>
          <FormItem<NonNullable<typeof filters>> name="campaignId" label="">
            <Dropdown
              placeholder="Campaign"
              grow={false}
              options={[
                { value: '', label: 'All Campaigns' },
                ...meta.campaigns,
              ]}
            />
          </FormItem>
        </Form>
        <Button icon="refresh" loading={reloading} onClick={refresh}>
          Refresh
        </Button>
      </TopPanel>
      <div className="flex-1 overflow-y-auto">
        <Table
          data={nodes}
          loading={loading}
          sort={sort}
          onSort={setSort}
          config={{
            columns: [
              {
                header: 'Contact',
                sortName: 'contact_email',
                cellClassName: 'text-small min-w-[20rem]',
                getValue: ({ contact_name, contact_email, contact_phone }) => (
                  <div>
                    <div className="text-text">{contact_name}</div>
                    <div className="text-tiny text-textLight">
                      {contact_email}
                    </div>
                    <div className="text-tiny text-textLight">
                      {contact_phone}
                    </div>
                  </div>
                ),
              },
              {
                header: 'State',
                sortName: 'latest_contact_state',
                cellClassName: 'max-w-[15rem]',
                getValue: ({
                  latest_contact_state,
                  latest_contact_state_enum,
                }) => (
                  <Slug
                    className="max-w-[13rem]"
                    text={latest_contact_state || 'No State'}
                    color={getColor(latest_contact_state_enum)}
                  />
                ),
              },
              {
                header: 'Next Task',
                sortName: 'next_task_time',
                cellClassName: 'text-small',
                getValue: ({ next_task_time, next_task_channel }) => (
                  <div className="flex flex-col">
                    <div>{next_task_channel}</div>
                    <div className="text-tiny text-textLight">
                      {formatDate(next_task_time)}
                    </div>
                  </div>
                ),
              },
              {
                header: 'Campaign',
                sortName: 'campaign_name',
                cellClassName: 'text-small',
                getValue: ({ campaign_name }) => <Slug text={campaign_name} />,
              },
              {
                header: 'Actions',
                align: 'right',
                cellClassName: 'min-w-[20rem]',
                getValue: item => (
                  <Button
                    icon="question"
                    width="small"
                    tooltip="Contact State Details"
                    onClick={() => handleView(item.contact_id)}
                    loading={loadingView && contactId === item.contact_id}
                  />
                ),
              },
            ],
          }}
        />
      </div>
    </div>
  );
};

export default CampaignMonitoring;
